import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Col, Button, Input, Select, Checkbox } from 'antd';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { Task } from 'vl-common/src/types';
import { Datestamp } from 'vl-common/src/components/Datestamp';
import useFetchClientList from 'vl-common/src/hooks/useFetchClientList';
import { useMembershipNumberChecker } from 'vl-common/src/components/AddPatient/AddPatient';
import { CheckmarkIcon, ErrorIcon } from 'react-hot-toast';
import { PatientTags } from 'vl-common/src/components/PatientTags/PatientTags';
import {
  useCompleteTaskModalEligibilityData,
  useCompleteTaskModalSubmission
} from 'vl-common/src/components/Tasks/hooks/useCompleteTaskModal';
import { z } from 'zod';
import useUserType from 'vl-common/src/hooks/useUserType';
import { tools } from 'vl-common/src/lib/service.tools';
import useIsReferrerAdmin from 'vl-common/src/hooks/useIsReferrerAdmin';
import useIsReferrer from 'vl-common/src/hooks/useIsReferrer';

interface CompleteTaskModalProps {
  show: boolean; //isOpen
  selectedTask: Task | undefined; //selectTask
  onClose: () => void;
  onCompleted: () => void; // split from onClose
  onSaveComplete: () => void; // split from onClose
}

const CompleteTaskModalContent = ({
  selectedTask,
  onClose,
  onCompleted,
  onSaveComplete
}: Omit<CompleteTaskModalProps, 'show'>) => {
  const { t } = useTranslation('common');
  const [form] = Form.useForm();
  const userType = useUserType();
  const clientList = useFetchClientList();
  const isReferrerAdmin = useIsReferrerAdmin();
  const isReferrer = useIsReferrer();
  const membershipChecker = useMembershipNumberChecker(clientList);
  const isEligibilityCheck = selectedTask?.task_type_code === 'ELIGIBILITY';
  const [claimStatus, setClaimStatus] = useState('PENDING');
  const [pendingClaimStatus, setPendingClaimStatus] = useState(true);
  const [hasClaimStatusChanged, setClaimStatusChanged] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });
  const [membershipValid, setMembershipValid] = useState({
    valid: false,
    message: ''
  });

  const isSubmittingEligibilityRef = useRef(false);

  const policyCheckFormValue = Form.useWatch('policy_checks_fulfilled', form);
  const virtualOnlyCheckApplied = policyCheckFormValue?.includes('VIRTUAL_ONLY');
  const authorizedWithVirtualOnly = claimStatus === 'AUTHORISED' && virtualOnlyCheckApplied;

  const patientEligibilitySchema = z.object({
    membership_no: z
      .string()
      .refine(() => membershipValid.valid, { message: membershipValid?.message || 'Invalid membership number' })
      .refine((value) => !tools.isEmptyOrNull(value), { message: 'Membership number is required' }),
    case_ref: z
      .string()
      .optional()
      .refine((value) => (claimStatus === 'AUTHORISED' || claimStatus === 'PENDING' ? !!value : true), {
        message: 'Case Reference is required for authorised or pending claims'
      }),
    info_tags: z.array(z.string()).optional(),
    filter_tags: z.array(z.string()).optional(),
    claim_status: z.string().refine((value) => !!value, { message: 'Claim status is required' }),
    policy_checks_fulfilled: z.array(z.string()).optional(),
    virtual_only_reason: z
      .string()
      .optional()
      .refine((value) => (authorizedWithVirtualOnly ? !!value : true), {
        message: 'Virtual Only Reason is required'
      }),
    referrer_notes: z.string().optional()
  });

  const {
    isFormDataLoaded,
    patientData,
    currentTaskStatus,
    caseMeta,
    eligibilityCheckStatuses,
    eligibilityPolicyCheckList,
    appliedEligibilityPolicies,
    referrerNotes
  } = useCompleteTaskModalEligibilityData({ selectedTask, isEligibilityCheck });

  const virtualOnlyReasonOptions = caseMeta?.virtual_only_reason_codes.map((reason) => ({
    value: reason.virtual_only_reason_code,
    label: reason.virtual_only_reason_desc
  }));

  const isMembershipNoDisabled = patientData?.ro_fields.includes('membership_no') && (isReferrerAdmin || isReferrer);

  const {
    isEligibilitySaveLoading,
    isUpdateEligibilitySuccess,
    performEligibilitySave,
    completeTask,
    isCompleteTaskLoading
  } = useCompleteTaskModalSubmission({
    selectedTask,
    claimStatus
  });

  const clearFormErrors = () => {
    const fields = form.getFieldsValue();
    const fieldKeys = Object.keys(fields);
    const clearErrors = fieldKeys.map((key) => ({ name: key, errors: [] }));
    form.setFields(clearErrors);
  };

  const isEligibilityFormValid = () => {
    try {
      return patientEligibilitySchema.parse(form.getFieldsValue());
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorFields = error.issues.map((issue) => ({
          name: issue.path[0],
          errors: [issue.message]
        }));

        form.setFields(errorFields);
        return false;
      }
    }
  };

  const onEligibilitySave = async () => {
    if (isSubmittingEligibilityRef.current) return;
    isSubmittingEligibilityRef.current = true;

    clearFormErrors();
    const validatedData = isEligibilityFormValid();
    try {
      if (validatedData && selectedTask?.task_id && selectedTask?.patient_guid) {
        await performEligibilitySave({
          validatedData,
          eligibilityPolicyCheckList,
          case_id: selectedTask?.case_id || undefined
        }); // Case ref included in validatedData
        if (form.getFieldValue('shouldComplete')) {
          // shouldComplete = true when Completed clicked - task is closed
          onTaskComplete();
        } else {
          // shouldComplete = false when only Saved is clicked - task isn't completed
          onSaveComplete();
        }
        return true;
      }
    } catch (error: any) {
      displayAlertMessage(error?.message || 'Error saving data, please try again later');
      return false;
    } finally {
      isSubmittingEligibilityRef.current = false;
    }
  };

  const onTaskComplete = async () => {
    if (selectedTask?.task_id && selectedTask?.patient_guid) {
      try {
        await completeTask();
        onCompleted();
      } catch (e: any) {
        displayAlertMessage(e?.message || 'Error completing task, please try again later');
      }
    } else {
      displayAlertMessage('Error completing task, please try again later');
    }
  };

  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
    const t = setTimeout(() => {
      setShowAlertMessage({ visibility: false, msg });
      clearTimeout(t);
    }, 5000);
  };

  const onTagChange = (key: any) => form.setFieldValue('info_tags', key);
  const onFilterChange = (key: any) => form.setFieldValue('filter_tags', key);

  useEffect(() => {
    // Check validity of membership number on initial load
    try {
      if (patientData?.client_id && patientData?.membership_no) {
        const { message: msg, valid } = membershipChecker({
          client_id: patientData?.client_id,
          membership_no: patientData?.membership_no
        });
        setMembershipValid({ message: msg, valid });
      }
    } catch (error) {
      console.debug('Error checking membership number', error);
    }
  }, [patientData, isFormDataLoaded, membershipChecker]);

  useEffect(() => {
    if (form && isFormDataLoaded) {
      form.resetFields();
      form.setFieldsValue({
        membership_no: patientData?.membership_no || '',
        case_ref: selectedTask?.client_case_id,
        claim_status: currentTaskStatus || 'PENDING',
        policy_checks_fulfilled: appliedEligibilityPolicies || [],
        referrer_notes: referrerNotes,
        shouldComplete: undefined
      });
      setClaimStatus(currentTaskStatus || 'PENDING');
    }

    return () => {
      setClaimStatus('PENDING');
      setPendingClaimStatus(true);
      setClaimStatusChanged(false);
      setMembershipValid({ valid: false, message: '' });
      setShowAlertMessage({ visibility: false, msg: '' });
    };
  }, [form, isFormDataLoaded]);

  useEffect(() => {
    // Everytime claim status is changed, check whether it is a 'pending' status
    // e.g PENDING or AWA_ADD_INFO
    // Also check if claim status has been changed for patient notified text
    const status = caseMeta?.eligibility_check_status_codes?.find(
      (status) => status.eligibility_check_status_code === claimStatus
    );

    setPendingClaimStatus(!!status?.pending_state);

    const savedStatus = currentTaskStatus || 'PENDING';
    setClaimStatusChanged(claimStatus !== savedStatus);
  }, [claimStatus, caseMeta, currentTaskStatus]);

  const patientDetailsURL = `#/${
    {
      REFERRER: 'referrer',
      ADMIN: 'referrer-admin',
      CLIENTADMIN: 'admin'
    }[userType as 'REFERRER' | 'ADMIN' | 'CLIENTADMIN']
  }/patient-details?patient-guid=${patientData?.patient_guid}`;

  return (
    <Modal
      forceRender
      title={`Complete Task: ${selectedTask?.task_id} `}
      open
      okButtonProps={{ disabled: isEligibilityCheck }}
      width="798px"
      onCancel={onClose}
      data-testid="complete-task-modal"
      footer={[
        <Button
          data-testid="complete-task-cancel"
          key="cancel"
          type="default"
          onClick={onClose}
          style={{ fontWeight: '400' }}
        >
          {t('Cancel')}
        </Button>,
        isEligibilityCheck && (
          <Button
            data-testid="complete-task-save"
            key="submit"
            type="default"
            onClick={() => {
              // shouldComplete = false means task is saved but not completed
              form.setFieldValue('shouldComplete', false);
              form.submit();
            }}
            loading={isEligibilitySaveLoading}
            disabled={!pendingClaimStatus || isCompleteTaskLoading || isUpdateEligibilitySuccess}
            style={{ fontWeight: '400' }}
          >
            {t('Save')}
          </Button>
        ),
        <Button
          data-testid="complete-task-complete"
          key="third"
          type="primary"
          onClick={async () => {
            if (isEligibilityCheck) {
              // shouldComplete = true means task is saved and completed
              form.setFieldValue('shouldComplete', true);
              form.submit();
            } else {
              onTaskComplete();
            }
          }}
          loading={isCompleteTaskLoading}
          disabled={
            isUpdateEligibilitySuccess ||
            isCompleteTaskLoading ||
            (isEligibilityCheck && (pendingClaimStatus || isEligibilitySaveLoading))
          }
          style={{ fontWeight: '400' }}
        >
          {t('Complete')}
        </Button>
      ]}
    >
      <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
      {/* Generic Message */}
      {!isEligibilityCheck && (
        <div
          style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            alignItems: 'baseline',
            gridColumn: 'span 2'
          }}
        >
          <h3 style={{ fontSize: '16px' }}>{t('Are you sure you wish to complete this task?')}</h3>
        </div>
      )}
      {/* Eligibility Form */}
      {isEligibilityCheck && patientData && isFormDataLoaded && form && (
        <Col>
          <Form
            form={form}
            onFinish={onEligibilitySave}
            labelCol={{ span: 16 }}
            wrapperCol={{ span: 24 }}
            layout="vertical"
            style={{
              margin: '12px 0%',
              width: '100%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px'
            }}
            initialValues={{
              membership_no: patientData?.membership_no,
              case_ref: selectedTask?.client_case_id,
              claim_status: 'PENDING',
              policy_checks_fulfilled: appliedEligibilityPolicies || [],
              referrer_notes: referrerNotes
            }}
          >
            <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline', gridColumn: 'span 2' }}>
              <div>
                <h3 style={{ fontSize: '16px', color: 'red' }}>
                  {t('Confirm')}{' '}
                  <>
                    {selectedTask.cat_desc} {selectedTask.area_desc}{' '}
                  </>
                  {t('Eligibility For:')}
                </h3>
                <span>
                  <b>
                    <a href={patientDetailsURL}>{patientData?.full_name}</a>
                  </b>{' '}
                  ({<Datestamp date={patientData?.dob} format="Date only (localised) - 03/02/2022" />} -{' '}
                  {patientData?.postcode})
                </span>
              </div>
            </div>
            <Form.Item
              name="membership_no"
              label={t('Patient Reference')}
              data-testid="complete-task-membership-no"
              required
            >
              <Input
                disabled={isMembershipNoDisabled}
                onChange={(e) => {
                  const { message: msg, valid } = membershipChecker({
                    client_id: patientData?.client_id,
                    membership_no: e.target.value
                  });
                  setMembershipValid({ message: msg, valid });
                }}
                suffix={membershipValid.valid ? <CheckmarkIcon /> : <ErrorIcon />}
              />
            </Form.Item>
            <Form.Item name="case_ref" label={t('Case Reference')} data-testid="complete-task-case-ref" required>
              <Input />
            </Form.Item>
            {/* Not and Input */}
            <div style={{ gridColumn: 'span 2', width: '100%' }}>
              <h3 style={{ fontSize: '14px' }}>{t('Patient Notes for Insurer')}</h3>
              <Input.TextArea style={{ color: 'black' }} disabled value={selectedTask?.task_notes || ''} />
            </div>
            {/* Add info tags to patient */}
            <Form.Item name="info_tags" label={t('Add info tags to patient')}>
              <PatientTags
                data-testid="complete-task-info-tags"
                patient_guid={selectedTask?.patient_guid}
                insured={patientData?.insured}
                updateTags={onTagChange}
                displayAlertMessage={displayAlertMessage}
              />
            </Form.Item>
            {/* Add filter tags to patient */}
            <Form.Item name="filter_tags" label={t('Add filter tags to patient')}>
              <PatientTags
                data-testid="complete-task-filter-tags"
                patient_guid={selectedTask?.patient_guid}
                insured={patientData?.insured}
                updateTags={onFilterChange}
                displayAlertMessage={displayAlertMessage}
                isFilter
              />
            </Form.Item>
            {/* Claim Status */}
            <div>
              <Form.Item
                name="claim_status"
                label={t('Claim Status')}
                required
                style={{ marginBottom: hasClaimStatusChanged ? '0' : '24px' }}
              >
                <Select
                  options={eligibilityCheckStatuses}
                  placeholder={t('Select a claim status')}
                  style={{ width: '100%' }}
                  data-testid="complete-task-claim-status"
                  onChange={(value) => {
                    setClaimStatus(String(value));
                    form.setFieldValue('claim_status', value);
                  }}
                />
              </Form.Item>
              {hasClaimStatusChanged && (
                <span
                  style={{
                    fontSize: '12px',
                    color: '#f0ad4e'
                  }}
                >
                  {t('Patient will receive a notification regarding status change')}
                </span>
              )}
            </div>

            {/* Check Required */}
            <div>
              <Form.Item
                name="policy_checks_fulfilled"
                label={t('Required Checks')}
                required={eligibilityPolicyCheckList?.length > 0}
              >
                <Checkbox.Group
                  options={eligibilityPolicyCheckList}
                  style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginLeft: '1rem' }}
                  data-testid="complete-task-policy-checks"
                />
              </Form.Item>

              {authorizedWithVirtualOnly && (
                <Form.Item
                  name="virtual_only_reason"
                  label={t('Virtual Only Reason')}
                  required={authorizedWithVirtualOnly}
                >
                  <Select
                    options={virtualOnlyReasonOptions}
                    placeholder={t('Select Virtual Only Reason')}
                    data-testid="virtual-only-reason"
                  />
                </Form.Item>
              )}
            </div>
            {/* Referrer Notes */}
            <Form.Item name="referrer_notes" label={t('Referrer Notes')} style={{ gridColumn: 'span 2' }}>
              <Input.TextArea data-testid="complete-task-referrer-notes" placeholder={t('Enter notes')} />
            </Form.Item>
          </Form>
        </Col>
      )}
    </Modal>
  );
};

const CompleteTaskModal = ({ show, ...rest }: CompleteTaskModalProps) => show && <CompleteTaskModalContent {...rest} />;

export default CompleteTaskModal;
